import { PART_API_PATH } from "@/providers/SRP/constants";
import { useSrpApiContext } from "@/providers/SRP/provider";
import type { SrpRequestParams } from "@/providers/SRP/types";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import type { ZodSchema } from "zod";

/**
 * Custom hook to fetch multiple parts data with optional schema validation
 *
 * @template T - The expected return type
 * @param {boolean} enabled - Flag to enable/disable the queries
 * @param {SrpRequestParams[]} params - Array of request parameters
 * @param {ZodSchema<T>} [schema] - Optional Zod schema for response validation
 * @returns {UseQueryResult<T>[]} Array of query results
 * @throws {Error} When used outside of SrpApiContext
 */
export default function usePartMultiple<T>(
	enabled: boolean,
	params: SrpRequestParams[],
	schema?: ZodSchema<T>,
) {
	const context = useSrpApiContext();
	if (!context) {
		throw Error("SrpApiContext not found");
	}
	const { getFn } = context;
	return useQueries({
		queries: params.map((p) => ({
			enabled: enabled && params && params.length > 0,
			throwOnError: true,
			queryKey: [p],
			queryFn: async () => {
				const json = await getFn({ pathname: PART_API_PATH, params: p });
				return schema ? schema.parse(json) : (json as T);
			},
			placeholderData: keepPreviousData,
		})),
	});
}
